export interface PolicyItem {
  boldText?: string;
  text: string;
}

export interface PolicySubsection {
  title?: string;
  items: PolicyItem[];
}

export interface PolicySection {
  id: string;
  title: string;
  description?: string;
  subsections?: PolicySubsection[];
  listItems?: string[];
  footerText?: string;
}

export interface PrivacyPolicyData {
  header: {
    title: string;
    description: string;
    lastUpdated: string;
    effectiveDate: string;
    version: string;
    introText: string;
  };
  sections: PolicySection[];
  footerNote: string;
}

export const PRIVACY_POLICY_DATA: PrivacyPolicyData = {
  header: {
    title: 'Privacy Policy',
    description:
      'Welcome to the Charaka Practice Test Portal. Your privacy is important to us. This Privacy Policy explains how we collect, use, store, protect, and disclose your information when you access or use the Charaka Practice Test Portal.',
    lastUpdated: 'July 30, 2026',
    effectiveDate: 'July 30, 2026',
    version: '1.0',
    introText:
      'By accessing or using the Portal, you agree to the collection and use of your information in accordance with this Privacy Policy. If you do not agree with this policy, please discontinue using the portal.',
  },
  sections: [
    {
      id: '01',
      title: 'Information We Collect',
      description: 'To provide our services, we may collect the following information:',
      subsections: [
        {
          title: 'Personal Information',
          items: [
            { text: 'When you register on the portal, we may collect:' },
            { text: 'Full Name' },
            { text: 'Email Address' },
            { text: 'Mobile Number (if applicable)' },
            { text: 'College/Institution Name' },
            { text: 'Course or Academic Details (if applicable)' },
          ],
        },
        {
          title: 'Technical Information',
          items: [
            { text: 'When you use the Portal, we may automatically collect:' },
            { text: 'IP Address' },
            { text: 'Browser type and version' },
            { text: 'Device information' },
            { text: 'Operating System' },
            { text: 'Login date and time' },
            { text: 'Usage activity within the Portal' },
            { text: 'Cookies and similar technologies' },
          ],
        },
        {
          title: 'Test Activity',
          items: [
            { text: 'To provide mock test services, we may collect:' },
            { text: 'Test attempts' },
            { text: 'Test scores' },
            { text: 'Time spent during examinations' },
            { text: 'Answer selections' },
            { text: 'Performance reports' },
            { text: 'Progress history' },
          ],
        },
      ],
    },
    {
      id: '02',
      title: 'How We Use Your Information',
      description: 'We use your information to:',
      listItems: [
        'Create and manage your account.',
        'Provide access to mock tests.',
        'Track your test performance and progress.',
        'Improve portal functionality and user experience.',
        'Maintain platform security.',
        'Respond to your support requests.',
        'Send important service-related notifications.',
        'Comply with applicable legal obligations.',
      ],
      footerText:
        'We do not use your personal information for purposes unrelated to the services provided by the Portal without your consent.',
    },
    {
      id: '03',
      title: 'Cookies',
      description: 'The Portal may use cookies and similar technologies to:',
      listItems: [
        'Keep you signed in.',
        'Remember your preferences.',
        'Improve website performance.',
        'Analyse portal usage.',
        'Enhance user experience.',
      ],
      footerText:
        'You may disable cookies through your browser settings; however, certain features of the Portal may not function properly.',
    },
    {
      id: '04',
      title: 'Sharing of Information',
      description: 'We respect your privacy.',
      listItems: [
        'Your personal information will not be sold or rented to any third party.',
        'We may share your information only:',
        'With trusted service providers assisting in operating the portal.',
        'When required by law or a government authority.',
        'To protect the security, rights, or property of Charaka or its users.',
        'During business restructuring, merger, or acquisition where legally required.',
      ],
      footerText:
        'All third-party service providers are expected to maintain the confidentiality of your information.',
    },
    {
      id: '05',
      title: 'Data Security',
      description:
        'We implement reasonable administrative, technical, and organisational security measures to protect your personal information from:',
      listItems: [
        'Unauthorised access',
        'Loss',
        'Misuse',
        'Alteration',
        'Disclosure',
      ],
      footerText:
        'Although we take appropriate precautions, no method of electronic storage or internet transmission is completely secure. Therefore, we cannot guarantee absolute security.',
    },
    {
      id: '06',
      title: 'Data Retention',
      description: 'We retain your personal information only for as long as necessary to:',
      listItems: [
        'Maintain your account.',
        'Provide portal services.',
        'Generate performance reports.',
        'Meet legal and regulatory obligations.',
        'Resolve disputes and enforce our policies.',
      ],
      footerText:
        'When the information is no longer required, it will be securely deleted or anonymized wherever reasonably practicable.',
    },
    {
      id: '07',
      title: 'Your Rights',
      description: 'You may request to:',
      listItems: [
        'Access your personal information.',
        'Correct inaccurate information.',
        'Update your account details.',
        'Delete your account, subject to applicable legal requirements.',
        'Withdraw your consent where applicable.',
      ],
      footerText:
        'Requests may be submitted through the official support channels of the Portal.',
    },
    {
      id: '08',
      title: 'Third-Party Services',
      description:
        'The portal may contain links to third-party websites or services. Charaka is not responsible for the privacy practices or content of external websites. Users are encouraged to review the privacy policies of such websites before sharing any personal information.',
    },
    {
      id: '09',
      title: "Children's Privacy",
      description:
        'The Portal is intended primarily for students preparing for competitive examinations. Where users are below the age of 18 years, the portal should be used with the consent of a parent or legal guardian.',
    },
    {
      id: '10',
      title: 'Changes to this Privacy Policy',
      description:
        'Charaka reserves the right to update or modify this Privacy Policy at any time. Any changes will become effective immediately upon publication on the Portal. Continued use of the Portal after such updates constitutes acceptance of the revised Privacy Policy.',
    },
    {
      id: '11',
      title: 'Contact Us',
      description:
        'If you have any questions, concerns, or requests regarding this Privacy Policy or the handling of your personal information, please contact the Charaka Support Team through the official communication channels provided on the Portal.',
    },
  ],
  footerNote: '',
};
